import { useEffect, useRef, useState, useCallback, useMemo } from "react";

const AvatarFigure = ({ state = "dormant", color = "#002FA7", size = 200, interactive = true, className = "", level = 0 }) => {
  const svgRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [breathPhase, setBreathPhase] = useState(0);
  const animRef = useRef(null);

  // Breathing animation loop
  useEffect(() => {
    let start = null;
    const animate = (ts) => {
      if (!start) start = ts;
      setBreathPhase((ts - start) / 1000);
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  // Eye tracking
  const handleMouseMove = useCallback((e) => {
    if (!interactive || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (window.innerWidth / 2);
    const dy = (e.clientY - cy) / (window.innerHeight / 2);
    setMousePos({ x: Math.max(-1, Math.min(1, dx)), y: Math.max(-1, Math.min(1, dy)) });
  }, [interactive]);

  useEffect(() => {
    if (!interactive) return;
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [handleMouseMove, interactive]);

  // Derive a lighter and darker shade from the base color
  const colorHSL = useMemo(() => {
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  }, [color]);

  const lightColor = `hsl(${colorHSL.h}, ${Math.min(colorHSL.s + 10, 100)}%, ${Math.min(colorHSL.l + 25, 92)}%)`;
  const paleColor = `hsl(${colorHSL.h}, ${Math.min(colorHSL.s + 5, 100)}%, ${Math.min(colorHSL.l + 35, 96)}%)`;
  const darkColor = `hsl(${colorHSL.h}, ${colorHSL.s}%, ${Math.max(colorHSL.l - 15, 10)}%)`;

  const breath = Math.sin(breathPhase * 1.5) * 0.015;
  const eyeX = mousePos.x * 1.8;
  const eyeY = mousePos.y * 1.2;

  // State configs - brighter overall for better visibility
  const stateConfig = {
    dormant: { flameOpacity: 0.6, sparkles: 0, eyeOpen: 0.55, mouthType: "sad", bodyOpacity: 0.75, cheekOpacity: 0.2, glowSize: 6 },
    awakening: { flameOpacity: 0.8, sparkles: 3, eyeOpen: 0.85, mouthType: "curious", bodyOpacity: 0.9, cheekOpacity: 0.35, glowSize: 10 },
    clear: { flameOpacity: 0.95, sparkles: 6, eyeOpen: 1, mouthType: "smile", bodyOpacity: 1, cheekOpacity: 0.45, glowSize: 14 },
    radiant: { flameOpacity: 1, sparkles: 9, eyeOpen: 1, mouthType: "happy", bodyOpacity: 1, cheekOpacity: 0.55, glowSize: 18 },
  };
  const cfg = stateConfig[state] || stateConfig.dormant;

  // Generate sparkle particles
  const sparkles = [];
  for (let i = 0; i < cfg.sparkles; i++) {
    const angle = (i / cfg.sparkles) * Math.PI * 2 + breathPhase * 0.4;
    const r = 32 + Math.sin(breathPhase * 0.7 + i * 2) * 8;
    const sx = 50 + Math.cos(angle) * r;
    const sy = 45 + Math.sin(angle) * r;
    const sOp = 0.3 + Math.sin(breathPhase * 1.2 + i) * 0.4;
    const sSize = 1.2 + Math.sin(breathPhase + i * 1.5) * 0.6;
    sparkles.push({ x: sx, y: sy, op: Math.max(0, sOp), size: sSize, key: i });
  }

  // Flame wisps animation
  const flameWave = (base, i) => base + Math.sin(breathPhase * 2 + i * 0.8) * 2;

  const uid = color.replace("#", "");

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 100 110"
      width={size}
      height={size}
      className={className}
      style={{ filter: `drop-shadow(0 0 ${cfg.glowSize}px ${color}50)` }}
      data-testid="avatar-figure"
    >
      <defs>
        <radialGradient id={`bodyGrad-${uid}`} cx="50%" cy="40%" r="55%">
          <stop offset="0%" stopColor={paleColor} />
          <stop offset="60%" stopColor={lightColor} />
          <stop offset="100%" stopColor={color} />
        </radialGradient>
        <radialGradient id={`headGrad-${uid}`} cx="45%" cy="35%" r="55%">
          <stop offset="0%" stopColor={paleColor} />
          <stop offset="50%" stopColor={lightColor} />
          <stop offset="100%" stopColor={color} />
        </radialGradient>
        <radialGradient id={`flameGrad-${uid}`} cx="50%" cy="100%" r="80%">
          <stop offset="0%" stopColor={lightColor} stopOpacity="0.9" />
          <stop offset="50%" stopColor={color} stopOpacity="0.6" />
          <stop offset="100%" stopColor={color} stopOpacity="0.1" />
        </radialGradient>
        <radialGradient id={`cheekGrad-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FF8FAB" stopOpacity={cfg.cheekOpacity} />
          <stop offset="100%" stopColor="#FF8FAB" stopOpacity="0" />
        </radialGradient>
        <filter id={`glow-${uid}`}>
          <feGaussianBlur stdDeviation="2" />
        </filter>
        <filter id={`softGlow-${uid}`}>
          <feGaussianBlur stdDeviation="1" />
        </filter>
      </defs>

      <g style={{ transform: `scale(${1 + breath})`, transformOrigin: "50px 60px" }} opacity={cfg.bodyOpacity}>

        {/* === FLAME HAIR === */}
        <g opacity={cfg.flameOpacity}>
          {/* Main center flame */}
          <path
            d={`M 50 28 C ${flameWave(46, 0)} 18, ${flameWave(42, 1)} 8, ${flameWave(48, 2)} ${flameWave(-2, 3)} C ${flameWave(52, 4)} ${flameWave(-5, 5)}, ${flameWave(55, 6)} 5, ${flameWave(50, 7)} ${flameWave(-1, 8)} C ${flameWave(45, 9)} 3, ${flameWave(58, 10)} 12, ${flameWave(54, 11)} 18 C ${flameWave(56, 12)} 22, 54 26, 50 28 Z`}
            fill={`url(#flameGrad-${uid})`}
          />
          {/* Left flame wisp */}
          <path
            d={`M 42 30 C ${flameWave(36, 2)} 22, ${flameWave(32, 3)} 14, ${flameWave(35, 4)} ${flameWave(5, 5)} C ${flameWave(38, 6)} 10, ${flameWave(44, 7)} 20, 42 30 Z`}
            fill={`url(#flameGrad-${uid})`}
            opacity="0.7"
          />
          {/* Right flame wisp */}
          <path
            d={`M 58 30 C ${flameWave(62, 5)} 22, ${flameWave(66, 6)} 14, ${flameWave(63, 7)} ${flameWave(5, 8)} C ${flameWave(60, 9)} 10, ${flameWave(56, 10)} 20, 58 30 Z`}
            fill={`url(#flameGrad-${uid})`}
            opacity="0.7"
          />
          {/* Small side wisps */}
          <path
            d={`M 38 33 C ${flameWave(30, 8)} 26, ${flameWave(28, 9)} 20, ${flameWave(33, 10)} ${flameWave(16, 11)} C ${flameWave(36, 12)} 22, 40 28, 38 33 Z`}
            fill={`url(#flameGrad-${uid})`}
            opacity="0.5"
          />
          <path
            d={`M 62 33 C ${flameWave(68, 11)} 26, ${flameWave(70, 12)} 20, ${flameWave(67, 13)} ${flameWave(16, 14)} C ${flameWave(64, 15)} 22, 60 28, 62 33 Z`}
            fill={`url(#flameGrad-${uid})`}
            opacity="0.5"
          />
        </g>

        {/* === HEAD (round, cute) === */}
        <ellipse cx="50" cy="44" rx="20" ry="18" fill={`url(#headGrad-${uid})`} />

        {/* Subtle head highlight */}
        <ellipse cx="44" cy="37" rx="8" ry="5" fill="white" fillOpacity="0.25" transform="rotate(-15, 44, 37)" />

        {/* === CHEEKS (rosy) === */}
        <ellipse cx="36" cy="49" rx="5" ry="3.5" fill={`url(#cheekGrad-${uid})`} />
        <ellipse cx="64" cy="49" rx="5" ry="3.5" fill={`url(#cheekGrad-${uid})`} />

        {/* === EYES === */}
        <g>
          {/* Left eye */}
          <g>
            {/* Eye white */}
            <ellipse cx={43 + eyeX * 0.3} cy={43} rx="5" ry={4.5 * cfg.eyeOpen} fill="white" />
            {/* Iris */}
            <ellipse cx={43 + eyeX} cy={43 + eyeY * 0.4} rx="3.2" ry={3.2 * cfg.eyeOpen} fill="#D4860B" />
            {/* Pupil */}
            <circle cx={43 + eyeX * 1.1} cy={43 + eyeY * 0.5} r={1.8 * cfg.eyeOpen} fill="#3D2200" />
            {/* Eye highlight */}
            <circle cx={44.5 + eyeX * 0.5} cy={41.5} r={1 * cfg.eyeOpen} fill="white" fillOpacity="0.9" />
            <circle cx={42 + eyeX * 0.5} cy={44} r={0.5 * cfg.eyeOpen} fill="white" fillOpacity="0.5" />
            {/* Eyelid for dormant state */}
            {state === "dormant" && (
              <ellipse cx={43} cy={41} rx="5.5" ry="3" fill={lightColor} />
            )}
            {/* Cute eyebrow */}
            <path
              d={state === "dormant"
                ? "M 39 38.5 Q 43 37.5 47 38.5"
                : state === "radiant" || state === "clear"
                ? "M 39 38 Q 43 36 47 38"
                : "M 39 38.5 Q 43 37 47 38.5"}
              stroke={darkColor}
              strokeWidth="0.7"
              fill="none"
              strokeLinecap="round"
            />
          </g>

          {/* Right eye */}
          <g>
            <ellipse cx={57 + eyeX * 0.3} cy={43} rx="5" ry={4.5 * cfg.eyeOpen} fill="white" />
            <ellipse cx={57 + eyeX} cy={43 + eyeY * 0.4} rx="3.2" ry={3.2 * cfg.eyeOpen} fill="#D4860B" />
            <circle cx={57 + eyeX * 1.1} cy={43 + eyeY * 0.5} r={1.8 * cfg.eyeOpen} fill="#3D2200" />
            <circle cx={58.5 + eyeX * 0.5} cy={41.5} r={1 * cfg.eyeOpen} fill="white" fillOpacity="0.9" />
            <circle cx={56 + eyeX * 0.5} cy={44} r={0.5 * cfg.eyeOpen} fill="white" fillOpacity="0.5" />
            {state === "dormant" && (
              <ellipse cx={57} cy={41} rx="5.5" ry="3" fill={lightColor} />
            )}
            <path
              d={state === "dormant"
                ? "M 53 38.5 Q 57 37.5 61 38.5"
                : state === "radiant" || state === "clear"
                ? "M 53 38 Q 57 36 61 38"
                : "M 53 38.5 Q 57 37 61 38.5"}
              stroke={darkColor}
              strokeWidth="0.7"
              fill="none"
              strokeLinecap="round"
            />
          </g>
        </g>

        {/* === MOUTH === */}
        {cfg.mouthType === "sad" && (
          <path d="M 45 53 Q 50 51 55 53" stroke={darkColor} strokeWidth="0.8" fill="none" strokeLinecap="round" />
        )}
        {cfg.mouthType === "curious" && (
          <ellipse cx="50" cy="52.5" rx="3" ry="2" fill={darkColor} fillOpacity="0.6" />
        )}
        {cfg.mouthType === "smile" && (
          <path d="M 44 51 Q 50 55.5 56 51" stroke={darkColor} strokeWidth="0.9" fill="none" strokeLinecap="round" />
        )}
        {cfg.mouthType === "happy" && (
          <g>
            <path d="M 43 51 Q 50 57 57 51" stroke={darkColor} strokeWidth="0.9" fill={darkColor} fillOpacity="0.3" strokeLinecap="round" />
            {/* Tongue/inner mouth */}
            <ellipse cx="50" cy="53.5" rx="3" ry="1.5" fill="#FF8FAB" fillOpacity="0.5" />
          </g>
        )}

        {/* === SMALL BODY === */}
        <path
          d="M 40 60 C 38 60 34 66 35 74 C 35.5 78 38 82 42 82 L 58 82 C 62 82 64.5 78 65 74 C 66 66 62 60 60 60 Z"
          fill={`url(#bodyGrad-${uid})`}
        />
        {/* Body highlight */}
        <ellipse cx="47" cy="68" rx="5" ry="7" fill="white" fillOpacity="0.15" transform="rotate(-10, 47, 68)" />

        {/* === ARMS (stubby, cute) === */}
        {/* Left arm */}
        <path
          d={state === "radiant"
            ? "M 36 65 C 30 62 26 58 28 55"
            : "M 36 65 C 32 67 28 72 30 75"}
          stroke={color}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          opacity="0.8"
        />
        {/* Left hand */}
        <circle
          cx={state === "radiant" ? 28 : 30}
          cy={state === "radiant" ? 55 : 75}
          r="2.5"
          fill={lightColor}
        />
        {/* Right arm */}
        <path
          d={state === "radiant"
            ? "M 64 65 C 68 62 72 58 70 55"
            : state === "clear"
            ? "M 64 65 C 68 63 72 60 71 57"
            : "M 64 65 C 68 67 72 72 70 75"}
          stroke={color}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          opacity="0.8"
        />
        {/* Right hand */}
        <circle
          cx={state === "radiant" ? 70 : state === "clear" ? 71 : 70}
          cy={state === "radiant" ? 55 : state === "clear" ? 57 : 75}
          r="2.5"
          fill={lightColor}
        />

        {/* === FEET (small round) === */}
        <ellipse cx="44" cy="83" rx="5" ry="2.5" fill={color} opacity="0.7" />
        <ellipse cx="56" cy="83" rx="5" ry="2.5" fill={color} opacity="0.7" />

        {/* === CORE STAR (on chest, like reference) === */}
        <g transform="translate(50, 70)" opacity={0.5 + cfg.flameOpacity * 0.5}>
          <polygon
            points="0,-3.5 1,-1 3.5,-1 1.5,0.5 2.5,3 0,1.5 -2.5,3 -1.5,0.5 -3.5,-1 -1,-1"
            fill="#FFD700"
            fillOpacity={0.6 + Math.sin(breathPhase * 2) * 0.2}
          />
        </g>
      </g>

      {/* === SPARKLE PARTICLES === */}
      {sparkles.map((s) => (
        <g key={s.key} transform={`translate(${s.x}, ${s.y})`} opacity={s.op}>
          {/* 4-pointed star sparkle */}
          <polygon
            points={`0,${-s.size * 1.5} ${s.size * 0.4},${-s.size * 0.4} ${s.size * 1.5},0 ${s.size * 0.4},${s.size * 0.4} 0,${s.size * 1.5} ${-s.size * 0.4},${s.size * 0.4} ${-s.size * 1.5},0 ${-s.size * 0.4},${-s.size * 0.4}`}
            fill="#FFD700"
          />
        </g>
      ))}

      {/* === ORBITAL RINGS for "clear" and "radiant" states === */}
      {(state === "clear" || state === "radiant") && (
        <g opacity={0.2 + Math.sin(breathPhase * 0.8) * 0.1}>
          <ellipse
            cx="50" cy="44" rx="28" ry="8"
            fill="none"
            stroke={lightColor}
            strokeWidth="0.4"
            strokeDasharray="3 2"
            transform={`rotate(${-20 + breathPhase * 8}, 50, 44)`}
          />
          {state === "radiant" && (
            <ellipse
              cx="50" cy="44" rx="32" ry="10"
              fill="none"
              stroke={lightColor}
              strokeWidth="0.3"
              strokeDasharray="2 3"
              transform={`rotate(${15 - breathPhase * 5}, 50, 44)`}
            />
          )}
        </g>
      )}

      {/* === DORMANT: cloudy wisps around === */}
      {state === "dormant" && (
        <g opacity={0.15 + Math.sin(breathPhase * 0.5) * 0.05}>
          <ellipse cx={30 + Math.sin(breathPhase * 0.3) * 3} cy="55" rx="8" ry="4" fill="#9CA3AF" filter={`url(#glow-${uid})`} />
          <ellipse cx={70 + Math.sin(breathPhase * 0.4) * 3} cy="52" rx="7" ry="3.5" fill="#9CA3AF" filter={`url(#glow-${uid})`} />
          <ellipse cx={50 + Math.sin(breathPhase * 0.2) * 2} cy="30" rx="6" ry="3" fill="#9CA3AF" filter={`url(#glow-${uid})`} />
        </g>
      )}

      {/* === LEVEL CROWN (level 5+) === */}
      {level >= 5 && (
        <g opacity={0.7 + Math.sin(breathPhase * 0.8) * 0.15}>
          {/* Crown shape above head */}
          <polygon
            points="40,6 43,12 46,4 50,11 54,4 57,12 60,6 58,14 42,14"
            fill="#FFD700"
            fillOpacity={0.6 + level * 0.04}
            stroke="#FFA500"
            strokeWidth="0.3"
          />
          {level >= 8 && (
            <>
              <circle cx="46" cy="6" r="1" fill="white" fillOpacity="0.8" />
              <circle cx="50" cy="4.5" r="1.2" fill="white" fillOpacity="0.9" />
              <circle cx="54" cy="6" r="1" fill="white" fillOpacity="0.8" />
            </>
          )}
        </g>
      )}

      {/* === LEVEL AURA (level 3+) === */}
      {level >= 3 && (
        <ellipse
          cx="50" cy="50" rx={35 + level * 2} ry={30 + level * 1.5}
          fill="none"
          stroke={color}
          strokeWidth={0.3 + level * 0.05}
          strokeOpacity={0.1 + Math.sin(breathPhase * 0.6) * 0.08}
          strokeDasharray={level >= 7 ? "none" : "4 3"}
        />
      )}

      {/* === LEVEL STARS (level 2+, number = level/2) === */}
      {level >= 2 && Array.from({ length: Math.min(Math.floor(level / 2), 5) }).map((_, i) => {
        const starAngle = (i / Math.min(Math.floor(level / 2), 5)) * Math.PI * 2 + breathPhase * 0.2;
        const starR = 42 + level;
        const sx = 50 + Math.cos(starAngle) * starR;
        const sy = 45 + Math.sin(starAngle) * starR * 0.7;
        const sOp = 0.3 + Math.sin(breathPhase + i * 2) * 0.25;
        return (
          <polygon
            key={`lvl-star-${i}`}
            points={`${sx},${sy - 2} ${sx + 0.7},${sy - 0.7} ${sx + 2},${sy} ${sx + 0.7},${sy + 0.7} ${sx},${sy + 2} ${sx - 0.7},${sy + 0.7} ${sx - 2},${sy} ${sx - 0.7},${sy - 0.7}`}
            fill="#FFD700"
            opacity={sOp}
          />
        );
      })}
    </svg>
  );
};

export default AvatarFigure;
